import * as fs from "node:fs";
import * as path from "node:path";
import { fileURLToPath } from "node:url";
import type { ExtensionAPI, ExtensionContext } from "@lpb-work/pi-coding-agent";
import { parseFrontmatter } from "../agents/frontmatter.ts";
import type { SubagentParamsLike } from "../runs/foreground/subagent-executor.ts";
import { getAgentDir, getProjectConfigDir } from "../shared/utils.ts";

interface PromptWorkflow {
	name: string;
	description: string;
	argumentHint?: string;
	body: string;
	filePath: string;
	agent: string;
	context?: "fresh" | "fork";
	model?: string;
	skill?: string | string[] | false;
	cwd?: string;
	chain?: string;
}

type PromptWorkflowRunner = (params: SubagentParamsLike, ctx: ExtensionContext) => Promise<void>;

const RESERVED_COMMAND_NAMES = new Set([
	"chain-prompts",
	"prompt-workflow",
	"run",
	"chain",
	"parallel",
	"run-chain",
	"subagents-doctor",
	"subagents-models",
]);

function packagePromptsDir(): string {
	return path.resolve(path.dirname(fileURLToPath(import.meta.url)), "..", "..", "prompts");
}

function isPackagedPromptWorkflow(workflow: PromptWorkflow): boolean {
	const relative = path.relative(packagePromptsDir(), workflow.filePath);
	return relative !== "" && relative !== ".." && !relative.startsWith(`..${path.sep}`) && !path.isAbsolute(relative);
}

function promptDirs(cwd: string): string[] {
	return [packagePromptsDir(), path.join(getAgentDir(), "prompts"), path.join(getProjectConfigDir(cwd), "prompts")];
}

function readPromptFiles(cwd: string): string[] {
	const files: string[] = [];
	for (const dir of promptDirs(cwd)) {
		let entries: fs.Dirent[];
		try {
			entries = fs.readdirSync(dir, { withFileTypes: true });
		} catch (error) {
			if (error instanceof Error && "code" in error && (error as NodeJS.ErrnoException).code === "ENOENT") continue;
			throw new Error(
				`Failed to read prompt workflow directory '${dir}': ${error instanceof Error ? error.message : String(error)}`,
			);
		}
		for (const entry of entries) {
			if (entry.isFile() && entry.name.endsWith(".md")) files.push(path.join(dir, entry.name));
		}
	}
	return files;
}

function firstNonEmptyLine(value: string): string {
	return (
		value
			.split(/\r?\n/)
			.map((line) => line.trim())
			.find(Boolean) ?? "Prompt workflow"
	);
}

function stringField(frontmatter: Record<string, string>, key: string): string | undefined {
	const value = frontmatter[key]?.trim();
	return value ? value : undefined;
}

function booleanField(frontmatter: Record<string, string>, key: string): boolean | undefined {
	const value = frontmatter[key]?.trim().toLowerCase();
	if (value === "true" || value === "yes" || value === "1") return true;
	if (value === "false" || value === "no" || value === "0") return false;
	return undefined;
}

function parseSkill(value: string | undefined): string | string[] | false | undefined {
	if (!value) return undefined;
	if (value === "false") return false;
	const parts = value
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	return parts.length > 1 ? parts : parts[0];
}

function parseAgent(frontmatter: Record<string, string>): string {
	const subagent = stringField(frontmatter, "subagent");
	if (!subagent || subagent === "true") return "delegate";
	return subagent;
}

function loadPromptWorkflow(filePath: string): PromptWorkflow | undefined {
	let content: string;
	try {
		content = fs.readFileSync(filePath, "utf-8");
	} catch (error) {
		throw new Error(
			`Failed to read prompt workflow '${filePath}': ${error instanceof Error ? error.message : String(error)}`,
		);
	}
	const normalized = content.replace(/\r\n/g, "\n");
	if (normalized.startsWith("---\n") && !/\n---(?:\n|$)/.test(normalized.slice(4))) {
		throw new Error(`Invalid frontmatter in prompt workflow '${filePath}': missing closing '---'.`);
	}
	let frontmatter: Record<string, string>;
	let body: string;
	try {
		({ frontmatter, body } = parseFrontmatter(content));
	} catch (error) {
		throw new Error(
			`Invalid frontmatter in prompt workflow '${filePath}': ${error instanceof Error ? error.message : String(error)}`,
		);
	}
	const name = path.basename(filePath, ".md");
	if (!name || RESERVED_COMMAND_NAMES.has(name)) return undefined;
	const model = stringField(frontmatter, "model");
	const skill = parseSkill(stringField(frontmatter, "skill"));
	const cwd = stringField(frontmatter, "cwd");
	const chain = stringField(frontmatter, "chain");
	const argumentHint = stringField(frontmatter, "argument-hint");
	return {
		name,
		description: stringField(frontmatter, "description") ?? firstNonEmptyLine(body),
		...(argumentHint ? { argumentHint } : {}),
		body,
		filePath,
		agent: parseAgent(frontmatter),
		...(booleanField(frontmatter, "inheritContext") === true || booleanField(frontmatter, "fork") === true
			? { context: "fork" as const }
			: {}),
		...(booleanField(frontmatter, "fresh") === true ? { context: "fresh" as const } : {}),
		...(model ? { model } : {}),
		...(skill !== undefined ? { skill } : {}),
		...(cwd ? { cwd } : {}),
		...(chain ? { chain } : {}),
	};
}

export function discoverPromptWorkflows(cwd: string): PromptWorkflow[] {
	const workflows = new Map<string, PromptWorkflow>();
	for (const file of readPromptFiles(cwd)) {
		const workflow = loadPromptWorkflow(file);
		if (workflow) workflows.set(workflow.name, workflow);
	}
	return [...workflows.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function shellWords(input: string): string[] {
	const words: string[] = [];
	let current = "";
	let quote: "'" | '"' | undefined;
	let escaped = false;
	for (const ch of input) {
		if (escaped) {
			current += ch;
			escaped = false;
			continue;
		}
		if (ch === "\\") {
			escaped = true;
			continue;
		}
		if (quote) {
			if (ch === quote) quote = undefined;
			else current += ch;
			continue;
		}
		if (ch === "'" || ch === '"') {
			quote = ch;
			continue;
		}
		if (/\s/.test(ch)) {
			if (current) {
				words.push(current);
				current = "";
			}
			continue;
		}
		current += ch;
	}
	if (current) words.push(current);
	return words;
}

/**
 * Substitute prompt arguments using the same one-pass syntax as coding-agent.
 * Defaults and argument values are emitted literally; they are never recursively expanded.
 */
export function substituteArgs(template: string, args: string[]): string {
	const allArgs = args.join(" ");
	return template.replace(
		/\$\{(\d+|ARGUMENTS|@):-([^}]*)\}|\$\{@:(\d+)(?::(\d+))?\}|\$(ARGUMENTS|@|\d+)/g,
		(_match, defaultTarget, defaultValue, sliceStart, sliceLength, simple) => {
			if (defaultTarget) {
				const value =
					defaultTarget === "@" || defaultTarget === "ARGUMENTS"
						? allArgs
						: args[Number(defaultTarget) - 1];
				return value ? value : defaultValue;
			}
			if (sliceStart) {
				let start = Number(sliceStart) - 1;
				if (start < 0) start = 0;
				if (sliceLength) return args.slice(start, start + Number(sliceLength)).join(" ");
				return args.slice(start).join(" ");
			}
			if (simple === "ARGUMENTS" || simple === "@") return allArgs;
			return args[Number(simple) - 1] ?? "";
		},
	);
}

function parseRuntimeOptions(words: string[]): {
	args: string[];
	agentOverride?: string;
	fork?: boolean;
	fresh?: boolean;
	bg?: boolean;
	autofix?: boolean;
} {
	const args: string[] = [];
	let agentOverride: string | undefined;
	let fork = false;
	let fresh = false;
	let bg = false;
	let autofix = false;
	for (let i = 0; i < words.length; i++) {
		const word = words[i]!;
		if (word === "autofix") {
			autofix = true;
			continue;
		}
		if (word === "--fork") {
			fork = true;
			continue;
		}
		if (word === "--fresh") {
			fresh = true;
			continue;
		}
		if (word === "--bg" || word === "--async") {
			bg = true;
			continue;
		}
		if (word === "--subagent") {
			agentOverride = words[++i];
			continue;
		}
		const eq = word.match(/^--subagent(?:=|:)(.+)$/);
		if (eq) {
			agentOverride = eq[1];
			continue;
		}
		args.push(word);
	}
	return { args, agentOverride, fork, fresh, bg, autofix };
}

function splitPromptChain(input: string): string[] {
	return input
		.split(" -> ")
		.map((part) => part.trim())
		.filter(Boolean);
}

function workflowParams(
	workflow: PromptWorkflow,
	args: string[],
	runtime: ReturnType<typeof parseRuntimeOptions>,
): SubagentParamsLike {
	const task = substituteArgs(workflow.body, args).trim();
	const context = runtime.fork ? "fork" : runtime.fresh ? "fresh" : workflow.context;
	const autofixAgent =
		runtime.autofix &&
		isPackagedPromptWorkflow(workflow) &&
		(workflow.name === "parallel-review" || workflow.name === "parallel-cleanup")
			? "workflow-worker"
			: workflow.agent;
	return {
		agent: runtime.agentOverride ?? autofixAgent,
		task,
		agentScope: "both",
		...(context ? { context } : {}),
		...(workflow.model ? { model: workflow.model } : {}),
		...(workflow.skill !== undefined ? { skill: workflow.skill } : {}),
		...(workflow.cwd ? { cwd: workflow.cwd } : {}),
	};
}

function promptWorkflowExecutionParams(
	workflows: PromptWorkflow[],
	args: string[],
	runtime: ReturnType<typeof parseRuntimeOptions>,
): SubagentParamsLike {
	return {
		workflowScript: promptWorkflowScript(workflows, args, runtime),
		agentScope: "both",
		async: !!runtime.bg,
	};
}

function promptWorkflowScript(
	workflows: PromptWorkflow[],
	args: string[],
	runtime: ReturnType<typeof parseRuntimeOptions>,
): string {
	const launches = workflows.map((workflow, index) => {
		const params = workflowParams(workflow, args, runtime);
		const task = params.task ?? "";
		const child = {
			agent: params.agent ?? "delegate",
			task,
			...(params.model ? { model: params.model } : {}),
			...(params.skill !== undefined ? { skill: params.skill } : {}),
			...(params.cwd ? { cwd: params.cwd } : {}),
			...(params.context ? { context: params.context } : {}),
		};
		return `const step${index} = await runs.run(${JSON.stringify(`prompt-${index + 1}-${workflow.name}`)}, { ...${JSON.stringify(child)}, task: ${JSON.stringify(task)}.replaceAll("{previous}", previous) });\nprevious = step${index}.output;`;
	});
	return `let previous = "";\n${launches.join("\n")}\nreturn previous;`;
}

function findWorkflow(workflows: PromptWorkflow[], name: string): PromptWorkflow | undefined {
	return workflows.find((workflow) => workflow.name === name);
}

function formatWorkflowList(workflows: PromptWorkflow[]): string {
	const usage = [
		"Usage: /prompt-workflow <name> [args...] [autofix] [--fork|--fresh] [--bg] [--subagent <agent>]",
		"Template arguments: $1/$2, $@/$ARGUMENTS, ${N:-default}, ${@:-default}/${ARGUMENTS:-default}, ${@:N}, ${@:N:L}.",
	];
	if (workflows.length === 0) return ["No prompt workflows found in package, user, or project prompts.", ...usage].join("\n");
	return [
		"Prompt workflows:",
		...workflows.map(
			(workflow) =>
				`- ${workflow.name}${workflow.argumentHint ? ` ${workflow.argumentHint}` : ""}: ${workflow.description} (${workflow.filePath})`,
		),
		"",
		...usage,
	].join("\n");
}

export function registerPromptWorkflowCommands(input: {
	pi: ExtensionAPI;
	run: PromptWorkflowRunner;
	getCwd?: () => string;
}): void {
	const { pi, run, getCwd } = input;
	let lastCwd = getCwd?.() || process.cwd();
	pi.registerCommand("prompt-workflow", {
		description: "Run a prompt template through native pi-subagents: /prompt-workflow <name> [args]",
		getArgumentCompletions: (prefix) => {
			const namePrefix = prefix.trimStart().split(/\s/u)[0] ?? "";
			return discoverPromptWorkflows(getCwd?.() || lastCwd)
				.filter((workflow) => workflow.name.startsWith(namePrefix))
				.map((workflow) => ({
					value: workflow.name,
					label: workflow.name,
					description: workflow.argumentHint
						? `${workflow.argumentHint} — ${workflow.description}`
						: workflow.description,
				}));
		},
		handler: async (rawArgs, ctx) => {
			lastCwd = ctx.cwd;
			try {
				const words = shellWords(rawArgs);
				const name = words.shift();
				const workflows = discoverPromptWorkflows(ctx.cwd);
				if (!name || name === "list") {
					pi.sendMessage({ content: formatWorkflowList(workflows), display: true } as Parameters<
						typeof pi.sendMessage
					>[0]);
					return;
				}
				const workflow = findWorkflow(workflows, name);
				if (!workflow) {
					ctx.ui.notify(`Unknown prompt workflow: ${name}. Use /prompt-workflow list for available templates.`, "error");
					return;
				}
				const runtime = parseRuntimeOptions(words);
				if (workflow.chain) {
					const chain = splitPromptChain(workflow.chain).map((stepName) => {
						const step = findWorkflow(workflows, stepName);
						if (!step) throw new Error(`Unknown prompt workflow in chain '${workflow.name}': ${stepName}`);
						return step;
					});
					await run(promptWorkflowExecutionParams(chain, runtime.args, runtime), ctx);
					return;
				}
				await run(promptWorkflowExecutionParams([workflow], runtime.args, runtime), ctx);
			} catch (error) {
				ctx.ui.notify(error instanceof Error ? error.message : String(error), "error");
			}
		},
	});
}
